/**
 * dataCache - Cache léger "stale-while-revalidate" en mémoire.
 *
 * Objectif : navigations quasi-instantanées en réaffichant les données déjà consultées,
 * sans refetch inutile (TTL) et sans appels en double (déduplication des requêtes en vol).
 *
 * - getCached(key)            -> données en cache (ou undefined)
 * - swrFetch(key, fetcher)    -> renvoie le cache si frais (< ttl), sinon fetch (dédupliqué)
 * - prefetch(key, fetcher)    -> précharge en arrière-plan (survol/affichage), best-effort
 * - invalidate / invalidatePrefix -> à appeler après une mutation pour forcer un refresh
 * - clearCache()              -> à appeler au logout (évite toute fuite entre comptes)
 *
 * Aucune règle métier ici : c'est une simple couche au-dessus des appels API existants.
 */

const store = new Map();    // key -> { data, ts }
const inflight = new Map(); // key -> Promise

export const DEFAULT_TTL = 45000; // 45s

export function getCached(key) {
    const e = store.get(key);
    return e ? e.data : undefined;
}

export function setCached(key, data) {
    store.set(key, { data, ts: Date.now() });
}

export function isFresh(key, ttl = DEFAULT_TTL) {
    const e = store.get(key);
    return !!e && (Date.now() - e.ts < ttl);
}

/**
 * Renvoie les données en cache si elles sont fraîches, sinon exécute le fetcher.
 * Les appels concurrents sur la même clé partagent la même promesse (mutualisation).
 */
export async function swrFetch(key, fetcher, { ttl = DEFAULT_TTL, force = false } = {}) {
    if (!force && isFresh(key, ttl)) return store.get(key).data;
    if (inflight.has(key)) return inflight.get(key);
    const p = (async () => {
        try {
            const data = await fetcher();
            setCached(key, data);
            return data;
        } finally {
            inflight.delete(key);
        }
    })();
    inflight.set(key, p);
    return p;
}

/** Précharge en arrière-plan (best-effort). N'agit que si pas déjà frais / pas déjà en vol. */
export function prefetch(key, fetcher, { ttl = DEFAULT_TTL } = {}) {
    if (isFresh(key, ttl) || inflight.has(key)) return;
    swrFetch(key, fetcher, { ttl }).catch(() => { /* silencieux */ });
}

export function invalidate(key) {
    store.delete(key);
    inflight.delete(key);
}

export function invalidatePrefix(prefix) {
    for (const k of Array.from(store.keys())) {
        if (k.startsWith(prefix)) store.delete(k);
    }
    for (const k of Array.from(inflight.keys())) {
        if (k.startsWith(prefix)) inflight.delete(k);
    }
}

export function clearCache() {
    store.clear();
    inflight.clear();
}
